import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import { useAggregateAPIOperationStatus, useQuery } from "@octopusdeploy/octopus-react-client";
import type { GetSharedSpacesToProjectTemplateBySlugResponse, GitRef, ProjectTemplateResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useState } from "react";
import semver from "semver/preload";
import type { TemplateWizardPageProps } from "~/areas/platformHub/TemplateWizardDialogLayout";
import TemplateWizardDialogLayout from "~/areas/platformHub/TemplateWizardDialogLayout";
import type { ReleaseType } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import { PublishTemplateDialog } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import { ShareTemplateDialog, ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import type { CommitParams, PublishParams, SaveProjectTemplateParams, ShareParams } from "~/areas/platformHub/projectTemplates/useSaveProjectTemplate";
import { CommitSummaryAndDetails } from "~/areas/projects/components/VersionControl/CommitDialog";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import CustomDialog from "~/components/Dialog/CustomDialog";
import type { ProjectTemplateVersion } from "~/routing/pageRegistrations/ProjectTemplatePageRegistration";
import { PlatformHubTemplateType } from "../platformHubHelpers";
export enum ActionCompleted {
    Committed = "committed",
    Published = "published",
    Shared = "shared"
}
function getShareType(sharedSpaces: GetSharedSpacesToProjectTemplateBySlugResponse): ShareTemplateSpacesType {
    if (sharedSpaces.SharedToAllSpaces) {
        return ShareTemplateSpacesType.ShareAll;
    }
    if (sharedSpaces.IndividuallySharedSpaceIds.length > 0) {
        return ShareTemplateSpacesType.ShareIndividual;
    }
    return ShareTemplateSpacesType.NotShared;
}
export enum TemplateWizardSteps {
    "Commit",
    "Publish",
    "Share"
}
export interface PublishWizardVersion {
    Version: string;
    IsPreRelease: boolean;
}
interface ProjectTemplateWizardDialogProps {
    projectTemplate: ProjectTemplateResource;
    gitRef: GitRef;
    defaultBranch: string;
    stepsToShow: TemplateWizardSteps[];
    getDefaultCommitMessage: () => string;
    onClose: () => void;
    onInitialSuccessCallout: () => void;
    publishedVersions: ProjectTemplateVersion[];
    executeSave: (params: SaveProjectTemplateParams) => Promise<void>;
}
export function ProjectTemplateWizardDialog({ projectTemplate, gitRef, defaultBranch, stepsToShow, getDefaultCommitMessage, onClose, onInitialSuccessCallout, publishedVersions, executeSave }: ProjectTemplateWizardDialogProps) {
    const showCommit = stepsToShow.includes(TemplateWizardSteps.Commit);
    const showPublish = stepsToShow.includes(TemplateWizardSteps.Publish);
    const showShare = stepsToShow.includes(TemplateWizardSteps.Share);
    const { isInProgress, errors } = useAggregateAPIOperationStatus();
    const emptyCommitMessage = { summary: "", details: "" };
    const defaultVersion = "1.0.0";
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
    const [shareType, setShareType] = useState<ShareTemplateSpacesType>(ShareTemplateSpacesType.NotShared);
    const [canSaveShare, setCanSaveShare] = useState<boolean>(false);
    const latestNonPreReleaseVersion = publishedVersions.find((v) => !semver.prerelease(v.Version));
    const [versionToPublish, setVersionToPublish] = useState<string>(latestNonPreReleaseVersion ? (semver.inc(latestNonPreReleaseVersion.Version, "major") ?? defaultVersion) : defaultVersion);
    const [releaseType, setReleaseType] = useState<ReleaseType>("Major");
    const [isPreRelease, setIsPreRelease] = useState<boolean>(false);
    const [releaseNotes, setReleaseNotes] = useState<string>("");
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>(emptyCommitMessage);
    const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState<boolean>(false);
    const { result: spaces } = useQuery(async (repository) => (showShare ? await repository.Spaces.all() : []), [showShare], "Get spaces for project template share");
    useQuery(async (repository) => {
        if (showShare) {
            const sharingConfiguration = await repository.ProjectTemplates.getSharedSpaces(projectTemplate.Slug);
            setSelectedSpaces(sharingConfiguration.IndividuallySharedSpaceIds);
            const resolvedShareType = getShareType(sharingConfiguration);
            setShareType(resolvedShareType);
        }
    }, [projectTemplate.Slug, showShare], "Get shared spaces for project template");
    const handleSave = async (isCommitOnly: boolean) => {
        const commitParams: CommitParams | undefined = showCommit
            ? {
                commitMessage: showCommit ? commitMessage : undefined,
            }
            : undefined;
        const publishParams: PublishParams | undefined = !isCommitOnly && showPublish && versionToPublish
            ? {
                versionToPublish,
                isPreRelease,
                releaseType,
                releaseNotes,
            }
            : undefined;
        const shareParams: ShareParams | undefined = !isCommitOnly && showShare && shareType && selectedSpaces
            ? {
                shareType,
                selectedSpaces,
            }
            : undefined;
        // State is set before the actual save as if there's an error from the save, we exit out of this function.
        setHasSaveBeenAttempted(true);
        await executeSave({ commitParams, publishParams, shareParams });
    };
    const pages: TemplateWizardPageProps[] = [];
    if (showCommit) {
        pages.push({
            title: "Commit",
            content: () => (<>
                    <CommitSummaryAndDetails onCommitMessageChanged={setCommitMessage} defaultSummary={getDefaultCommitMessage()} commitMessage={commitMessage} commitDetailsAccessibleName={`Commit details for saving the project template`} commitMessageAccessibleName={`Commit message for saving the project template`}/>

                    <p>
                        Octopus will commit the changes to the branch <code>{gitRef}</code>
                    </p>
                </>),
            actionAvailable: true,
            additionalActions: [
                <ActionButton key="Commit Only" label="Commit Only" disabled={isInProgress} onClick={async () => {
                        await handleSave(true);
                    }} type={ActionButtonType.Secondary}/>,
            ],
            statusBarLabel: "Commit",
            hideAdditionalActionsOnSinglePage: true,
            singlePageSaveLabel: "Commit",
            singlePageSavingLabel: "Committing...",
        });
    }
    if (showPublish) {
        pages.push({
            title: "Publish version",
            content: () => (<PublishTemplateDialog gitRef={gitRef} defaultBranch={defaultBranch} template={projectTemplate} templateType={PlatformHubTemplateType.ProjectTemplate} publishedVersions={publishedVersions} versionToPublish={versionToPublish} setVersionToPublish={setVersionToPublish} latestNonPreReleaseVersion={publishedVersions.find((v) => !semver.prerelease(v.Version))} isPreRelease={isPreRelease} setIsPreRelease={setIsPreRelease} releaseType={releaseType} setReleaseType={setReleaseType} releaseNotes={releaseNotes} setReleaseNotes={setReleaseNotes} hasUnsupportedSteps={false} // TODO
            />),
            actionAvailable: true,
            statusBarLabel: "Publish",
            singlePageSaveLabel: "Publish",
            singlePageSavingLabel: "Publishing...",
        });
    }
    if (showShare) {
        pages.push({
            title: "Share to...",
            content: () => <ShareTemplateDialog spaces={spaces ?? []} shareType={shareType} selectedSpaces={selectedSpaces} setShareType={setShareType} setSelectedSpaces={setSelectedSpaces} setCanSave={setCanSaveShare}/>,
            actionAvailable: canSaveShare,
            statusBarLabel: "Share",
            singlePageSaveLabel: "Share",
            singlePageSavingLabel: "Sharing...",
        });
    }
    const shouldShowErrors = errors && hasSaveBeenAttempted && !isInProgress;
    return (<CustomDialog open={true} close={noOp} render={() => (<TemplateWizardDialogLayout onSaveClick={async () => {
                await handleSave(false);
                return true;
            }} busy={isInProgress} errors={shouldShowErrors ? errors : undefined} savePermission={{ permission: Permission.PlatformHubEdit }} pages={pages}/>)}/>);
}
